import React, { useState } from "react";
import { Box, Fade } from "@mui/material";
import Typography from "@mui/material/Typography";
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from "react-router-dom";
import Badge from "@mui/material/Badge";
import AddIcon from "@mui/icons-material/Add";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Paper from "@mui/material/Paper";
import moment from "moment";
import RecipeGrid from "../recipe/recipe-grid";
import TitleBar from "../nav/title-bar";
import { useMealPlanner } from "../../context-providers/meal-planner-provider";
import { getMealsForDateRange, getMealsForDay } from "../../util/meal-helper";
import DateRangePicker from "../input/date-range-picker";
import generateShoppingList from "../../util/shopping-list-helper";
import { useShoppingListStore } from "../../context-providers/shopping-list-store-provider";
import { formatDateRange } from "../../util/date-helper";
import IntentButton, { Intent } from "../input/intent-button";
import { DEFAULT_FADE_ANIMATION } from "../../constants";
import { useHome } from "../../context-providers/home-provider";
import { ShoppingList } from "../../types";
import AutoPlanDialog from "./auto-plan-dialog";

const useStyles = makeStyles()(() => ({
  buttons: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  recipesButton: {
    width: "45%",
    margin: "10px auto",
    maxWidth: "300px",
  },
  autoPlanButton: {
    width: "95%",
    margin: "10px auto",
    maxWidth: "620px",
  },
  shoppingListButton: {
    width: "45%",
    margin: "10px auto",
    maxWidth: "300px",
  },
  summary: {
    textAlign: "center",
    padding: "5px 20px",
  },
  datePicker: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: "10px",
    backgroundColor: "transparent",
  },
}));

export default function Planner() {
  const { classes } = useStyles();
  const { userMeals, homeMeals, removeUserMeal, removeHomeMeal } =
    useMealPlanner();
  const { addHomeList, addUserList } = useShoppingListStore();
  const { home } = useHome();
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = selectedRange;
  const [autoPlanDialogOpen, setAutoPlanDialogOpen] = useState(false);

  const isHome = home && !!home.id;
  const onDeleteMeal = isHome ? removeHomeMeal : removeUserMeal;
  const meals = isHome ? homeMeals : userMeals;
  const addShoppingList = isHome ? addHomeList : addUserList;
  const selectedMeals = meals
    ? getMealsForDateRange(meals, selectedRange[0], selectedRange[1])
    : null;
  const numSelectedMeals = selectedMeals ? selectedMeals.length : 0;

  function selectDateRange(dateRange: any) {
    const [start, end] = dateRange;
    // Handle both Date objects and dayjs/moment objects
    const startDate = start instanceof Date ? start : start.toDate();
    const endDate = end instanceof Date ? end : end.toDate();
    setSelectedRange([startDate, endDate]);
  }

  function newShoppingList() {
    const filteredMeals = getMealsForDateRange(meals, startDate, endDate);
    const newList: ShoppingList = generateShoppingList(filteredMeals);
    newList.name = `Shopping List - ${formatSelectedDateRange()}`;
    addShoppingList(newList).then((newId: any) => {
      navigate(`/shopping/${newId}`);
    });
  }

  function formatSelectedDateRange() {
    return formatDateRange(startDate, endDate);
  }

  return (
    <Box>
      <TitleBar title="Meal Planner" />
      <br />
      <Fade in timeout={DEFAULT_FADE_ANIMATION}>
        <Box>
          <Typography className={classes.summary} variant="body2" gutterBottom>
            Select a date range below to show the meals planned for that period.
            You can change dates or servings for individual meals.
          </Typography>
          <Paper elevation={3} className={classes.datePicker}>
            <DateRangePicker
              dateRange={selectedRange}
              onChange={selectDateRange}
            />
          </Paper>
          <Box className={classes.buttons}>
            <IntentButton
              className={classes.recipesButton}
              onClick={() => navigate("/recipes")}
              variant="contained"
              intent={Intent.PRIMARY}
              startIcon={<AddIcon />}
            >
              Add Meals
            </IntentButton>
            <IntentButton
              disabled={numSelectedMeals <= 0}
              className={classes.shoppingListButton}
              onClick={() => newShoppingList()}
              variant="contained"
              intent={Intent.SECONDARY}
              startIcon={<PlaylistAddIcon />}
            >
              Shopping List
            </IntentButton>
            <IntentButton
              className={classes.autoPlanButton}
              onClick={() => setAutoPlanDialogOpen(true)}
              variant="outlined"
              intent={Intent.TERTIARY}
              startIcon={<AutoAwesomeIcon />}
            >
              Auto-Plan Week
            </IntentButton>
          </Box>
          <Typography className={classes.summary} variant="body2">
            {`Showing ${numSelectedMeals} meal${
              numSelectedMeals === 1 ? "" : "s"
            } for `}
            <strong>{formatSelectedDateRange()}</strong>
          </Typography>
          <RecipeGrid
            showOptions
            isMeals
            isHome={isHome}
            recipes={selectedMeals}
            onDelete={onDeleteMeal}
          />
        </Box>
      </Fade>
      <AutoPlanDialog
        open={autoPlanDialogOpen}
        onClose={() => setAutoPlanDialogOpen(false)}
      />
      <Box id="bottom-shim" style={{ height: "90px" }} />
    </Box>
  );
}
